import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supplierService } from '../services/supplier.service';

// Async thunks
export const fetchSuppliers = createAsyncThunk(
  'supplier/fetchSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      return await supplierService.getSuppliers();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des fournisseurs');
    }
  }
);

export const fetchActiveSuppliers = createAsyncThunk(
  'supplier/fetchActiveSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      return await supplierService.getActiveSuppliers();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des fournisseurs actifs');
    }
  }
);

export const fetchSupplierById = createAsyncThunk(
  'supplier/fetchSupplierById',
  async (id, { rejectWithValue }) => {
    try {
      return await supplierService.getSupplierById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération du fournisseur');
    }
  }
);

export const addSupplier = createAsyncThunk(
  'supplier/addSupplier',
  async (supplier, { rejectWithValue }) => {
    try {
      const result = await supplierService.createSupplier(supplier);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la création du fournisseur');
    }
  }
);

export const editSupplier = createAsyncThunk(
  'supplier/editSupplier',
  async ({ id, supplier }, { rejectWithValue }) => {
    try {
      await supplierService.updateSupplier(id, supplier);
      return { id, ...supplier };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la mise à jour du fournisseur');
    }
  }
);

export const removeSupplier = createAsyncThunk(
  'supplier/removeSupplier',
  async (id, { rejectWithValue }) => {
    try {
      await supplierService.deleteSupplier(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la suppression du fournisseur');
    }
  }
);

export const importSuppliers = createAsyncThunk(
  'supplier/importSuppliers',
  async (file, { rejectWithValue }) => {
    try {
      return await supplierService.importSuppliers(file);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'importation des fournisseurs');
    }
  }
);

// Initial state
const initialState = {
  suppliers: [],
  totalCount: 0,
  selectedSupplier: null,
  loading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
};

// Slice
const supplierSlice = createSlice({
  name: 'supplier',
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedSupplier: (state, action) => {
      state.selectedSupplier = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = action.payload;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch active suppliers
      .addCase(fetchActiveSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = action.payload;
      })
      .addCase(fetchActiveSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add supplier
      .addCase(addSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers.push(action.payload);
        state.showModal = false;
      })
      .addCase(addSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Edit supplier
      .addCase(editSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editSupplier.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.suppliers.findIndex(supplier => supplier.supplierId === action.payload.id);
        if (index !== -1) {
          state.suppliers[index] = action.payload;
        }
        state.showModal = false;
      })
      .addCase(editSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove supplier
      .addCase(removeSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeSupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = state.suppliers.filter(supplier => supplier.supplierId !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(removeSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Import suppliers
      .addCase(importSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importSuppliers.fulfilled, (state) => {
        state.loading = false;
        // Refresh suppliers list after import
      })
      .addCase(importSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedSupplier
} = supplierSlice.actions;

export default supplierSlice.reducer;